import { linearRegression, linearRegressionLine } from "simple-statistics";
import { DEMO_OBSERVATION_MONTH } from "./observation";
import type { Claim, ProblemRecord } from "./types";

export const CLAIM_PREDICTION_MONTHS = 6;
export const ILLUSTRATIVE_MONTHLY_GROWTH = 0.05;

export function correctiveActionPrediction(
  future: { month: string; claims: number; cost: number }[],
  implementation: string,
  reduction: number,
  addressable: number,
  lag: number,
) {
  return future.map((point) => {
    const elapsed =
      (Number(point.month.slice(0, 4)) - Number(implementation.slice(0, 4))) * 12 +
      Number(point.month.slice(5, 7)) -
      Number(implementation.slice(5, 7));
    const ramp = Math.min(1, Math.max(0, (elapsed - lag + 1) / 3));
    const remaining = Math.max(0, Math.round(point.claims * (1 - reduction * addressable * ramp)));
    return {
      ...point,
      remaining,
      withActionCost: point.claims ? (point.cost * remaining) / point.claims : 0,
    };
  });
}

export function predictionDirection(baseline: number, finalMonth: number) {
  if (baseline <= 0) return { label: "No baseline", changePct: null };
  const changePct = ((finalMonth - baseline) / baseline) * 100;
  return {
    label:
      finalMonth > baseline
        ? "Predicted worse"
        : finalMonth < baseline
          ? "Predicted better"
          : "Predicted stable",
    changePct,
  };
}

/** Explicit scenario assumptions, never recorded claim history or governed exposure. */
export function illustrativeIssuePrediction(
  record: ProblemRecord,
  asOf: string,
  growth = ILLUSTRATIVE_MONTHLY_GROWTH,
) {
  const baseline = record.claims / 12;
  const averageCost = record.claims > 0 ? record.financial.actualCost / record.claims : 0;
  const future = Array.from({ length: CLAIM_PREDICTION_MONTHS }, (_, i) => {
    const claims = Math.max(0, Math.round(baseline * (1 + growth) ** (i + 1)));
    return { month: monthOffset(asOf, i + 1), claims, cost: claims * averageCost };
  });
  return {
    record,
    baseline,
    averageCost,
    future,
    predictedClaims: future.reduce((sum, p) => sum + p.claims, 0),
    predictedCost: future.reduce((sum, p) => sum + p.cost, 0),
  };
}
const monthOffset = (month: string, offset: number) => {
  const [year, number] = month.split("-").map(Number);
  if (year === undefined || number === undefined) throw new Error("Invalid claim month");
  return new Date(Date.UTC(year, number - 1 + offset, 1)).toISOString().slice(0, 7);
};

/** Claim-level sample only; financial exposure is context, never a claim count input. */
export function predictIssueClaims(
  claims: Claim[],
  record: ProblemRecord,
  asOf = DEMO_OBSERVATION_MONTH,
) {
  const accepted = claims.filter(
    (c) =>
      c.recordId === record.id &&
      c.status !== "rejected" &&
      /^\d{4}-\d{2}-\d{2}$/.test(c.reportedDate),
  );
  const latest = accepted
    .map((c) => c.reportedDate.slice(0, 7))
    .sort()
    .at(-1);
  if (!latest) return null;
  const months = Array.from({ length: 6 }, (_, i) => monthOffset(latest, i - 5));
  const history = months.map((month) => ({
    month,
    claims: accepted.filter((c) => c.reportedDate.startsWith(month)).length,
  }));
  const activeMonths = history.filter((p) => p.claims > 0).length;
  const recent = accepted.filter((c) => months.includes(c.reportedDate.slice(0, 7)));
  const observedCost = accepted.reduce((sum, c) => sum + c.totalCost, 0);
  const averageCost = recent.length
    ? recent.reduce((sum, c) => sum + c.totalCost, 0) / recent.length
    : 0;
  const ageMonths =
    (Number(asOf.slice(0, 4)) - Number(latest.slice(0, 4))) * 12 +
    Number(asOf.slice(5, 7)) -
    Number(latest.slice(5, 7));
  const stale = ageMonths > 2;
  const sufficient = activeMonths >= 3 && recent.length >= 6 && !stale;
  const line = sufficient
    ? linearRegressionLine(linearRegression(history.map((p, i) => [i, p.claims])))
    : null;
  const future = line
    ? Array.from({ length: CLAIM_PREDICTION_MONTHS }, (_, i) => {
        const count = Math.max(0, Math.round(line(6 + Math.max(0, ageMonths) + i)));
        return { month: monthOffset(asOf, i + 1), claims: count, cost: count * averageCost };
      })
    : [];
  return {
    record,
    latest,
    history,
    future,
    sufficient,
    stale,
    observedClaims: accepted.length,
    observedCost,
    averageCost,
    predictedClaims: future.reduce((sum, p) => sum + p.claims, 0),
    predictedCost: future.reduce((sum, p) => sum + p.cost, 0),
  };
}
